import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);

  useEffect(() => {
    console.log('[AUTH] Initializing auth context...');
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const impersonating = localStorage.getItem('isImpersonating') === 'true';
    setIsImpersonating(impersonating);

    if (token && storedUser) {
      console.log('[AUTH] Found stored credentials, restoring session');
      setUser(JSON.parse(storedUser));
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      console.log('[AUTH] No stored credentials found');
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    console.log('[AUTH] Attempting login for:', email);
    try {
      const response = await api.post('/auth/login', { email, password });
      const { token, user: userData } = response.data;
      console.log('[AUTH] Login successful, user:', userData);
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(userData);
      return { success: true, user: userData };
    } catch (error) {
      console.error('[AUTH] Login failed:', error.response?.data || error.message);
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed'
      };
    }
  };

  const register = async (userData) => {
    console.log('[AUTH] Attempting registration for:', userData.email);
    try {
      const response = await api.post('/auth/register', userData);
      const { token, user: newUser } = response.data;
      console.log('[AUTH] Registration successful, user:', newUser);
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(newUser));
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(newUser);
      return { success: true, user: newUser };
    } catch (error) {
      console.error('[AUTH] Registration failed:', error.response?.data || error.message);
      return {
        success: false,
        error: error.response?.data?.error || 'Registration failed'
      };
    }
  };

  const impersonate = async (userId) => {
    try {
      const response = await api.post(`/admin/users/${userId}/impersonate`);
      const { token, user: targetUser } = response.data;

      if (!isImpersonating) {
        localStorage.setItem('admin_token', localStorage.getItem('token'));
        localStorage.setItem('admin_user', localStorage.getItem('user'));
      }

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(targetUser));
      localStorage.setItem('isImpersonating', 'true');

      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(targetUser);
      setIsImpersonating(true);
      return { success: true };
    } catch (error) {
      console.error('Failed to impersonate:', error);
      return { success: false, error: 'Failed to impersonate user' };
    }
  };

  const revertImpersonation = () => {
    if (!isImpersonating) return;

    const adminToken = localStorage.getItem('admin_token');
    const adminUser = localStorage.getItem('admin_user');

    if (adminToken && adminUser) {
      localStorage.setItem('token', adminToken);
      localStorage.setItem('user', adminUser);
      localStorage.removeItem('isImpersonating');
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');

      api.defaults.headers.common['Authorization'] = `Bearer ${adminToken}`;
      setUser(JSON.parse(adminUser));
      setIsImpersonating(false);
    } else {
      logout();
    }
  };

  const logout = () => {
    console.log('[AUTH] Logging out user:', user?.email);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('isImpersonating');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setIsImpersonating(false);
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading,
    impersonate,
    revertImpersonation,
    isImpersonating
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
